/**
 * Zoekopdrachten en rondes: wat de API en de webapp erover uitwisselen, en de
 * rekenkunde rond de Funda-zoek-URL.
 *
 * Een zoekopdracht is in de kern een URL. Wat je op het scherm ziet en bijstelt
 * zijn de *gebieden* erin - `amsterdam/de-pijp`, `amsterdam/oud-west` - en die
 * twee moeten in beide richtingen in elkaar over te zetten zijn: uit een geplakte
 * URL de gebieden lezen, en uit aangepaste gebieden weer een URL maken die Funda
 * begrijpt. Dat is pure tekstrekenkunde zonder database, en daarom staat het
 * hier en niet in de API.
 *
 * De parameter heet `selected_area`. Funda schrijft hem in de adresbalk als
 * JSON-array (`selected_area=["amsterdam/de-pijp"]`, URL-gecodeerd), maar in
 * opgevangen pagina-URL's komt ook de kale vorm voor
 * (`selected_area=amsterdam/frans-halsbuurt`). Allebei lezen, één vorm schrijven:
 * sinds MA-28 de **kommavorm** (`selected_area=1011,1012`). De JSON-vorm geeft
 * per pagina een server-redirect naar de kale vorm - twee HTML-verzoeken per
 * pagina - en de kommavorm niet (gemeten 9/10 oktober 2026).
 */
import { z } from 'zod';
import { isoDatumTijdSchema } from './common';

/* --- rondes ---------------------------------------------------------------- */

/**
 * Hoe een ronde afliep.
 *
 * `goed` is de enige soort waarna "stond er niet in" iets mag betekenen. De
 * andere drie zeggen allemaal: dit was geen volledige waarneming.
 */
export const RONDESOORTEN = ['goed', 'onvolledig', 'geblokkeerd', 'mislukt'] as const;
export const rondesoortSchema = z.enum(RONDESOORTEN);
export type Rondesoort = z.infer<typeof rondesoortSchema>;

export const RONDESOORT_LABELS: Record<Rondesoort, string> = {
  goed: 'gelopen',
  onvolledig: 'onvolledig',
  geblokkeerd: 'geblokkeerd',
  mislukt: 'mislukt',
};

/**
 * Hoe lang een zoekopdracht met rust wordt gelaten na een ronde van deze soort,
 * bovenop het gewone interval. Een blokkade (captcha) een dag: eerder opnieuw
 * proberen maakt hem alleen hardnekkiger. Een fout twee uur: lang genoeg om niet
 * elk half uur tegen dezelfde muur te lopen, kort genoeg om een haperende
 * verbinding niet een dag te laten kosten.
 *
 * Sinds MA-28 is dit uitstel per zoekopdracht niet meer het enige slot: een
 * blokkade zet ook de *globale* rust (zie `bepaalFundaRust`), want op 9 oktober
 * 2026 liep de wekker na een blokkade gewoon door naar de volgende zoekopdracht
 * en kreeg die dezelfde muur.
 */
export const RONDE_UITSTEL_MINUTEN: Record<Rondesoort, number> = {
  goed: 0,
  onvolledig: 0,
  geblokkeerd: 1440,
  mislukt: 120,
};

/* --- menselijk volume en globale rust (MA-28) ------------------------------ */

/**
 * Hoeveel pagina's één ronde hoogstens afloopt, als de extensie niets anders
 * ingesteld heeft. Twintig pagina's is driehonderd woningen; een mens die
 * zoekt bladert er ook niet meer door. De noodrem op de zoekopdracht zelf
 * (`max_paginas`, 80) blijft staan; de laagste van de twee geldt.
 */
export const RONDE_MAX_PAGINAS_STANDAARD = 20;

/**
 * Hoeveel pagina's er per dag (Amsterdamse tijd) bij Funda opgehaald mogen
 * worden, over alle zoekopdrachten samen. Geteld uit `zoekopdracht_run`; zit het
 * vol, dan is er niets meer aan de beurt tot de volgende dag. Op 9 oktober
 * 2026 kwam de blokkade na zestig pagina's in een kwartier.
 *
 * Sinds MA-32 is dit de *standaard*: het plafond zelf staat in `systeemstand`
 * (`funda_dagplafond`) en is op het tabblad Zoekopdrachten bij te stellen,
 * tussen `FUNDA_DAGPLAFOND_MIN` en `FUNDA_DAGPLAFOND_MAX`. Ontbreekt de sleutel
 * of staat er onzin, dan geldt deze waarde.
 */
export const FUNDA_DAGPLAFOND_PAGINAS = 80;
export const FUNDA_DAGPLAFOND_MIN = 10;
export const FUNDA_DAGPLAFOND_MAX = 300;
/**
 * Boven dit plafond zegt het scherm erbij dat de blokkade van 9 oktober na
 * zestig pagina's in een kwartier kwam: spreiding weegt zwaarder dan het totaal.
 */
export const FUNDA_DAGPLAFOND_WAARSCHUW_BOVEN = 100;

/**
 * Het dagplafond uit de tekst in `systeemstand`. Een geheel getal binnen de
 * grenzen telt; al het andere (ontbrekend, leeg, tekst, te klein, te groot)
 * valt terug op de standaard. Dat de database ooit iets buiten de grenzen
 * bevat is onwaarschijnlijk - de API controleert vooraf - maar een handmatig
 * gezette `0` mag de vanger niet voorgoed stilzetten.
 */
export function dagplafondUitWaarde(tekst: string | null | undefined): number {
  if (typeof tekst !== 'string' || tekst.trim() === '') return FUNDA_DAGPLAFOND_PAGINAS;
  const getal = Number(tekst);
  return Number.isInteger(getal) && getal >= FUNDA_DAGPLAFOND_MIN && getal <= FUNDA_DAGPLAFOND_MAX
    ? getal
    : FUNDA_DAGPLAFOND_PAGINAS;
}

/** PUT /beheer/funda-dagplafond: het dagplafond bijstellen. */
export const fundaDagplafondSchema = z.object({
  dagplafond: z.number().int().min(FUNDA_DAGPLAFOND_MIN).max(FUNDA_DAGPLAFOND_MAX),
});
export type FundaDagplafond = z.infer<typeof fundaDagplafondSchema>;

/* --- vaste tijden (MA-46) --------------------------------------------------- */

/**
 * Op vaste tijden, niet de hele middag door (MA-46).
 *
 * Op 11 en 12 oktober 2026 kwam de blokkade allebei de keren bij de zevende
 * ronde op rij: elk half uur een zoekopdracht, na zo'n veertig pagina's in
 * tweeënhalf à drie uur Access Denied. Niet het dagtotaal maar de reeks was te
 * lang. Sinds MA-46 is er alleen op een paar vaste tijden iets aan de beurt, en
 * dan in één lading tot een budget aan pagina's; daartussen ligt de vanger stil.
 *
 * De tijden staan als `UU:MM` in Amsterdamse tijd in `systeemstand`
 * (`funda_venster`, JSON), bij te stellen op het tabblad Zoekopdrachten.
 *
 * Sinds MA-62 mogen het er tot vierentwintig zijn: Feico wil de hele dag door
 * kleine porties (elk uur een groep van 6 tot 16 pagina's) in plaats van twee
 * grote. Een volle ronde van 59 pagina's in één keer gaf op 17-10 Access Denied
 * bij pagina 40; porties van 25 tot 32 kwamen de dagen ervoor gewoon door.
 */
export const FUNDA_VENSTER_STANDAARD = { tijden: ['12:15', '19:30'], paginas: 35 } as const;

/**
 * Hoe lang na een vaste tijd er nog iets aan de beurt mag zijn. De wekker van de
 * extensie tikt elk half uur; anderhalf uur geeft drie kansen, voor als de
 * eerste tik net voor het venster viel of de NAS even weg was. Liggen tijden
 * dichter op elkaar (elk uur, MA-62), dan telt de laatst begonnen; zie
 * `huidigVenster`.
 */
export const FUNDA_VENSTER_DUUR_MINUTEN = 90;
export const FUNDA_VENSTER_PAGINAS_MIN = 5;
export const FUNDA_VENSTER_PAGINAS_MAX = 60;
/** Tot MA-62 vier; elk uur van 08:00 tot 23:00 is er zestien. */
export const FUNDA_VENSTER_TIJDEN_MAX = 24;

/**
 * Een groep die korter dan dit geleden liep, komt in een volgend venster niet
 * opnieuw aan de beurt: tussen lunch en avond zit zeven uur, dus zo komt eerst
 * de rest van het zoekgebied langs.
 */
export const FUNDA_VENSTER_OPNIEUW_NA_UREN = 6;

/** Wat een zoekopdracht naar schatting kost als hij nog nooit goed liep. */
export const FUNDA_VENSTER_SCHATTING_ONBEKEND = 10;

const tijdSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Een tijd als 12:15.');

/** PUT /beheer/funda-venster en de stand in `systeemstand`. */
export const fundaVensterSchema = z.object({
  tijden: z
    .array(tijdSchema)
    .min(1, 'Minstens één tijd.')
    .max(FUNDA_VENSTER_TIJDEN_MAX, `Hoogstens ${FUNDA_VENSTER_TIJDEN_MAX} tijden.`)
    .transform((tijden) => [...new Set(tijden)].sort()),
  paginas: z.number().int().min(FUNDA_VENSTER_PAGINAS_MIN).max(FUNDA_VENSTER_PAGINAS_MAX),
});
export type FundaVenster = z.infer<typeof fundaVensterSchema>;

/** Het venster uit de tekst in `systeemstand`, of de standaard bij onzin of niets. */
export function vensterUitWaarde(tekst: unknown): FundaVenster {
  const standaard = {
    tijden: [...FUNDA_VENSTER_STANDAARD.tijden],
    paginas: FUNDA_VENSTER_STANDAARD.paginas,
  };
  if (typeof tekst !== 'string' || tekst.trim() === '') return standaard;
  try {
    const uitkomst = fundaVensterSchema.safeParse(JSON.parse(tekst));
    return uitkomst.success ? uitkomst.data : standaard;
  } catch {
    return standaard;
  }
}

/** Minuten na middernacht in Amsterdam, voor een tijdstip. */
export function amsterdamseMinuut(nu: Date): number {
  const delen = new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(nu);
  const uur = Number(delen.find((deel) => deel.type === 'hour')?.value ?? 0);
  const minuut = Number(delen.find((deel) => deel.type === 'minute')?.value ?? 0);
  return uur * 60 + minuut;
}

function minuutVan(tijd: string): number {
  const [uur, minuut] = tijd.split(':').map(Number);
  return (uur ?? 0) * 60 + (minuut ?? 0);
}

export type HuidigVenster = {
  /** De vaste tijd waar dit venster bij hoort, als `UU:MM`. */
  tijd: string;
  /** Wanneer het venster begon. */
  begin: Date;
};

/**
 * Het venster waar `nu` in valt, of null daarbuiten. Een venster loopt van de
 * vaste tijd tot `FUNDA_VENSTER_DUUR_MINUTEN` erna, ook over middernacht heen.
 */
export function huidigVenster(tijden: readonly string[], nu: Date): HuidigVenster | null {
  const minuut = amsterdamseMinuut(nu);
  let beste: { tijd: string; sinds: number } | null = null;
  for (const tijd of tijden) {
    const sinds = (minuut - minuutVan(tijd) + 1440) % 1440;
    /* Overlappen vensters (tijden dichter op elkaar dan de duur, MA-62), dan
       telt de laatst begonnen: anders zouden de pagina's van het vorige uur
       het budget van dit uur opmaken. */
    if (sinds < FUNDA_VENSTER_DUUR_MINUTEN && (beste === null || sinds < beste.sinds)) {
      beste = { tijd, sinds };
    }
  }
  if (beste === null) return null;
  const begin = new Date(nu.getTime() - beste.sinds * 60_000);
  begin.setUTCSeconds(0, 0);
  return { tijd: beste.tijd, begin };
}

/** De eerstvolgende vaste tijd na `nu`, als `UU:MM`; null zonder tijden. */
export function volgendeVensterTijd(tijden: readonly string[], nu: Date): string | null {
  if (tijden.length === 0) return null;
  const minuut = amsterdamseMinuut(nu);
  const gesorteerd = [...tijden].sort((a, b) => minuutVan(a) - minuutVan(b));
  return gesorteerd.find((tijd) => minuutVan(tijd) > minuut) ?? gesorteerd[0] ?? null;
}

/**
 * Welke zoekopdrachten er in één lading meegaan: in de gegeven volgorde (die het
 * langst niet liep eerst), zolang hun geschatte pagina's samen binnen het budget
 * blijven.
 *
 * Aan het begin van een venster (`eersteAltijd`) gaat de eerste altijd mee, ook
 * als hij alleen al groter is dan het budget: anders komt een grote groep nooit
 * meer aan de beurt. Later in hetzelfde venster niet meer (MA-47): dan zou een
 * volgende tik met nog één pagina over er een hele groep bovenop zetten.
 */
export function kiesLading<T extends { geschattePaginas: number }>(
  kandidaten: readonly T[],
  budget: number,
  eersteAltijd = true,
): T[] {
  if (budget <= 0) return [];
  const lading: T[] = [];
  let som = 0;
  for (const kandidaat of kandidaten) {
    const kost = Math.max(1, kandidaat.geschattePaginas);
    const magAlsEerste = lading.length === 0 && eersteAltijd;
    if (!magAlsEerste && som + kost > budget) continue;
    lading.push(kandidaat);
    som += kost;
    if (som >= budget) break;
  }
  return lading;
}

/**
 * GET /zoekopdrachten/vanger-planning (MA-47): of de vanger op de NAS nu aan hoort
 * te staan. Het planningsscript daar vraagt het elke vijf minuten, start de
 * container bij `aan` en zet hem weer uit als er niets meer te doen is en de
 * laatste ronde lang genoeg geleden was. Zo staan de vaste tijden op één plek:
 * de server.
 */
export const vangerPlanningSchema = z.object({
  aan: z.boolean(),
  /** Waarom niet, of null als hij aan hoort. */
  reden: z.string().nullable(),
  /** Wanneer de laatste ronde zich meldde, welke zoekopdracht ook. */
  laatsteRondeOp: isoDatumTijdSchema.nullable(),
  rustTot: isoDatumTijdSchema.nullable(),
});
export type VangerPlanning = z.infer<typeof vangerPlanningSchema>;

/* --- het blokkadelog (MA-46) ------------------------------------------------ */

/** Wat Funda liet zien: de captchapagina, een weigering, of iets wat de extensie niet herkende. */
export const BLOKKADESOORTEN = ['captcha', 'geweigerd', 'onbekend'] as const;
export type Blokkadesoort = (typeof BLOKKADESOORTEN)[number];

export const BLOKKADESOORT_LABELS: Record<Blokkadesoort, string> = {
  captcha: 'captcha',
  geweigerd: 'Access Denied',
  onbekend: 'onbekend',
};

/** Uit de melding van de extensie: "Akamai weigerde de toegang (Access Denied)" is een weigering. */
export function blokkadesoortVan(melding: string | null | undefined): Blokkadesoort {
  const tekst = (melding ?? '').toLowerCase();
  if (tekst.includes('access denied') || tekst.includes('weigerde')) return 'geweigerd';
  if (tekst.includes('captcha')) return 'captcha';
  return 'onbekend';
}

/**
 * Eén regel in het blokkadelog: alles wat er rond een blokkade bekend is, zodat
 * na een paar blokkades te zien is waar de grens ligt in plaats van te gokken.
 * De tellingen zijn pagina's uit `zoekopdracht_run`, de geblokkeerde ronde zelf
 * meegeteld.
 */
export const fundaBlokkadeSchema = z.object({
  id: z.uuid(),
  op: isoDatumTijdSchema,
  vanger: z.string().nullable(),
  zoekopdracht: z.string().nullable(),
  soort: z.enum(BLOKKADESOORTEN),
  melding: z.string().nullable(),
  /** Een captcha die op een mens wacht (NAS); die zet nog geen rust. */
  wachtOpMens: z.boolean(),
  /** Hoeveel pagina's deze zoekopdracht al had toen het misging. */
  pagina: z.number().int().nonnegative().nullable(),
  paginasUur: z.number().int().nonnegative(),
  paginasDrieUur: z.number().int().nonnegative(),
  paginasVandaag: z.number().int().nonnegative(),
  rondesVandaag: z.number().int().nonnegative(),
  /** Pagina's in het lopende venster, en wanneer dat begon; null buiten een venster. */
  paginasDezeKeer: z.number().int().nonnegative().nullable(),
  keerBegonnenOp: isoDatumTijdSchema.nullable(),
  /** Wanneer de ronde daarvoor begon, welke zoekopdracht ook. */
  vorigeRondeOp: isoDatumTijdSchema.nullable(),
  /** De trap van de rust die erop volgde, en tot wanneer; null zonder nieuwe rust. */
  trap: z.number().int().positive().nullable(),
  rustTot: isoDatumTijdSchema.nullable(),
  url: z.string().nullable(),
});
export type FundaBlokkade = z.infer<typeof fundaBlokkadeSchema>;

/** De pauze tussen twee pagina's, willekeurig in dit bereik (seconden). */
export const RONDE_PAUZE_SECONDEN = { min: 20, max: 45 } as const;

/**
 * De rust na een blokkade, in uren. Tot MA-62 een trap: een dag, drie dagen bij
 * een tweede binnen zeven dagen, en bij een derde binnen dertig dagen alles uit.
 * Die trap kostte meer dan Akamai zelf: een blokkade is daar de volgende dag
 * vergeten, en wij lagen er dagen uit. Sinds MA-62 is het twee uur, elke keer
 * (doorgaan tijdens een weigering heeft geen zin), en gaat er nooit iets uit.
 * De trap (hoeveelste blokkade in dertig dagen) blijft geteld voor het log.
 */
export const FUNDA_RUST_UREN = 2;
export const FUNDA_BLOKKADE_VENSTER_DAGEN = { tweede: 7, derde: 30 } as const;

export type FundaRustBesluit = {
  /** De hoeveelste blokkade dit is binnen het venster: 1, 2 of 3 (of meer). */
  trap: 1 | 2 | 3;
  rustTot: Date;
  /** Tot MA-62 bij de derde trap waar; nu altijd onwaar. Blijft voor het log en de API. */
  allesUit: boolean;
};

/**
 * Wat een nieuwe blokkade betekent, gegeven de eerdere blokkades.
 *
 * Pure rekenkunde: `eerdere` zijn de tijdstippen van eerdere blokkades (de
 * server bewaart er dertig dagen van), `nu` is het moment van deze. De trap
 * is sinds MA-62 alleen nog een telling voor het blokkadelog; de rust is
 * altijd even lang en er gaat niets uit.
 */
export function bepaalFundaRust(eerdere: readonly Date[], nu: Date): FundaRustBesluit {
  const binnen = (dagen: number) =>
    eerdere.filter((op) => {
      const verschil = nu.getTime() - op.getTime();
      return verschil >= 0 && verschil < dagen * 86_400_000;
    }).length;
  const rustTot = new Date(nu.getTime() + FUNDA_RUST_UREN * 3_600_000);

  if (binnen(FUNDA_BLOKKADE_VENSTER_DAGEN.derde) >= 2) return { trap: 3, rustTot, allesUit: false };
  if (binnen(FUNDA_BLOKKADE_VENSTER_DAGEN.tweede) >= 1)
    return { trap: 2, rustTot, allesUit: false };
  return { trap: 1, rustTot, allesUit: false };
}

/** GET /beheer/funda-rust: de globale rust en het dagtotaal, voor het tabblad Zoekopdrachten. */
export const fundaRustSchema = z.object({
  /** Tot wanneer er niets aan de beurt is; null als er geen rust loopt. */
  rustTot: isoDatumTijdSchema.nullable(),
  /** De blokkades van de afgelopen dertig dagen, nieuwste eerst. */
  blokkades: z.array(isoDatumTijdSchema),
  /** Pagina's die vandaag (Amsterdamse tijd) al bij Funda zijn opgehaald. */
  paginasVandaag: z.number().int().nonnegative(),
  /** Het dagplafond zoals het nu staat (`funda_dagplafond`, of de standaard). */
  dagplafond: z.number().int().positive(),
  /** De vaste tijden en het budget per keer (MA-46). */
  venster: fundaVensterSchema,
  /** De vaste tijd waar het nu in zit, of null daarbuiten. */
  vensterNu: z.string().nullable(),
  /** Pagina's in het lopende venster; 0 daarbuiten. */
  paginasDezeKeer: z.number().int().nonnegative(),
  /** De eerstvolgende vaste tijd. */
  volgendeTijd: z.string().nullable(),
});
export type FundaRust = z.infer<typeof fundaRustSchema>;

/**
 * De naam van de vanger die een ronde liep (MA-35): `kloud` voor de container
 * op de NAS, `laptop` voor de eigen pc. De extensie leest hem uit
 * `instellingen-lokaal.json` en stuurt hem mee; de server neemt hem over zoals
 * hij is, zonder standaard - ontbreekt hij, dan blijft hij leeg. Een naam is
 * kort en zonder vreemde tekens, want hij komt in een tabelkolom en een logregel.
 */
export const VANGER_NAAM_MAX = 40;
export const vangerNaamSchema = z.string().trim().min(1).max(VANGER_NAAM_MAX);

export const zoekopdrachtRunSchema = z.object({
  id: z.uuid(),
  begonnenOp: isoDatumTijdSchema,
  soort: rondesoortSchema,
  /** Pagina's die de extensie heeft bekeken; null als zij het niet meldde. */
  paginas: z.number().int().nonnegative().nullable(),
  /** Treffers vóór samenvouwen; een woning staat op meer dan één pagina. */
  treffers: z.number().int().nonnegative(),
  /** Wat Funda zelf zei dat er te vinden was (`hits.total`), als dat erbij zat. */
  verwacht: z.number().int().nonnegative().nullable(),
  woningen: z.number().int().nonnegative(),
  nieuw: z.number().int().nonnegative(),
  gewijzigd: z.number().int().nonnegative(),
  verdwenen: z.number().int().nonnegative(),
  melding: z.string().nullable(),
  /** Wie de ronde liep (`kloud`, `laptop`); null als de extensie geen naam meestuurde. */
  vanger: z.string().nullable(),
});
export type ZoekopdrachtRun = z.infer<typeof zoekopdrachtRunSchema>;

/**
 * Eén regel over een ronde, voor op het scherm en in `laatste_uitslag`.
 *
 * `61 pagina's, 412 woningen, 2 nieuw, 1 gewijzigd` voor een goede ronde;
 * bij de andere soorten staat de soort voorop en de melding erachter, want dan is
 * dát het nieuws en niet de telling.
 */
export function samenvatRun(run: {
  soort: Rondesoort;
  paginas: number | null;
  woningen: number;
  nieuw: number;
  gewijzigd: number;
  verdwenen: number;
  melding: string | null;
}): string {
  const delen: string[] = [];
  if (run.soort !== 'goed') delen.push(RONDESOORT_LABELS[run.soort]);
  if (run.paginas != null) delen.push(`${run.paginas} pagina${run.paginas === 1 ? '' : "'s"}`);
  if (run.soort === 'goed' || run.soort === 'onvolledig') {
    delen.push(`${run.woningen} woningen`);
    if (run.nieuw) delen.push(`${run.nieuw} nieuw`);
    if (run.gewijzigd) delen.push(`${run.gewijzigd} gewijzigd`);
    if (run.verdwenen) delen.push(`${run.verdwenen} verdwenen`);
  }
  if (run.melding) delen.push(run.melding);
  return delen.join(', ');
}

/* --- zoekopdracht ---------------------------------------------------------- */

export const zoekopdrachtSchema = z.object({
  id: z.uuid(),
  naam: z.string().min(1),
  bron: z.string().min(1),
  url: z.string().min(1),
  /** De `selected_area`-slugs uit de URL, bijv. `amsterdam/de-pijp`. */
  gebieden: z.array(z.string()),
  actief: z.boolean(),
  /** Gemaakt en bijgehouden door het zoekgebied (buurten op de kaart). */
  zoekgebied: z.boolean().default(false),
  intervalMinuten: z.number().int().positive(),
  maxPaginas: z.number().int().positive(),
  laatsteRunOp: isoDatumTijdSchema.nullable(),
  laatsteUitslag: z.string().nullable(),
  /** Tot wanneer de extensie hem met rust laat, na een blokkade of een fout. */
  uitgesteldTot: isoDatumTijdSchema.nullable(),
  laatsteRun: zoekopdrachtRunSchema.nullable(),
  /** Woningen die nu `actief` zijn in de feed van deze zoekopdracht. */
  aantalWoningen: z.number().int().nonnegative(),
  aangemaaktOp: isoDatumTijdSchema,
});
export type Zoekopdracht = z.infer<typeof zoekopdrachtSchema>;

/**
 * Een gebied-slug zoals Funda hem in `selected_area` zet: kleine letters, cijfers
 * en streepjes, met een schuine streep tussen gemeente en wijk of buurt.
 */
export const GEBIED_SLUG_PATROON = /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/;
export const gebiedSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(GEBIED_SLUG_PATROON, 'Een gebied ziet eruit als amsterdam/de-pijp.');

/** De grenzen van het ritme; dezelfde als de API al hanteerde voor de extensie. */
export const INTERVAL_MIN_MINUTEN = 30;
export const INTERVAL_MAX_MINUTEN = 10_080;

/**
 * Wat je in de webapp aan een zoekopdracht kunt veranderen. Allemaal optioneel:
 * een PATCH stuurt alleen wat er anders is.
 */
export const zoekopdrachtBewerkingSchema = z.object({
  naam: z.string().trim().min(1).max(120).optional(),
  gebieden: z.array(gebiedSlugSchema).min(1).max(60).optional(),
  actief: z.boolean().optional(),
  intervalMinuten: z.number().int().min(INTERVAL_MIN_MINUTEN).max(INTERVAL_MAX_MINUTEN).optional(),
});
export type ZoekopdrachtBewerking = z.infer<typeof zoekopdrachtBewerkingSchema>;

/** Een nieuwe zoekopdracht vanuit de webapp: naam en gebieden zijn verplicht. */
export const nieuweZoekopdrachtUitGebiedenSchema = z.object({
  naam: z.string().trim().min(1).max(120),
  gebieden: z.array(gebiedSlugSchema).min(1).max(60),
  intervalMinuten: z.number().int().min(INTERVAL_MIN_MINUTEN).max(INTERVAL_MAX_MINUTEN).optional(),
});
export type NieuweZoekopdrachtUitGebieden = z.infer<typeof nieuweZoekopdrachtUitGebiedenSchema>;

/* --- gebieden -------------------------------------------------------------- */

/** Een gebied zoals Funda het kent: de slug uit de URL plus een leesbare naam. */
export const gebiedSchema = z.object({
  slug: z.string().min(1),
  naam: z.string().min(1),
  /** `city`, `neighbourhood`, `district`, ... zoals Funda het noemt; vrije tekst. */
  soort: z.string().nullable(),
  /** De slug van het gebied waar dit onder valt, als dat bekend is. */
  ouder: z.string().nullable(),
});
export type Gebied = z.infer<typeof gebiedSchema>;

/* --- de URL ---------------------------------------------------------------- */

export const FUNDA_ZOEK_BASIS = 'https://www.funda.nl/zoeken/koop';
const SELECTED_AREA = 'selected_area';

/**
 * De gebieden uit een Funda-zoek-URL.
 *
 * Leest zowel de JSON-vorm (`["amsterdam/de-pijp","amsterdam/oud-west"]`) als
 * de kale vorm (`amsterdam/frans-halsbuurt`, eventueel door komma's gescheiden).
 * Een URL die niet te lezen is levert een lege lijst op, geen fout: een
 * zoekopdracht zonder gebied is nog altijd een zoekopdracht.
 */
export function gebiedenUitUrl(url: string | null | undefined): string[] {
  if (!url) return [];

  let adres: URL;
  try {
    adres = new URL(url);
  } catch {
    return [];
  }

  const ruw = adres.searchParams.get(SELECTED_AREA);
  if (!ruw) return [];

  const waarden = leesGebiedenWaarde(ruw);
  return uniek(waarden.map(schoonSlug).filter((slug): slug is string => slug !== null));
}

function leesGebiedenWaarde(ruw: string): string[] {
  const getrimd = ruw.trim();
  if (getrimd.startsWith('[')) {
    try {
      const lijst: unknown = JSON.parse(getrimd);
      if (Array.isArray(lijst)) return lijst.filter((x): x is string => typeof x === 'string');
    } catch {
      /* Geen JSON; dan is het de kale vorm met haakjes als ruis. */
    }
    return getrimd.replace(/^\[|\]$/g, '').split(',');
  }
  return getrimd.split(',');
}

function schoonSlug(waarde: string): string | null {
  const slug = waarde
    .trim()
    .replace(/^["']|["']$/g, '')
    .toLowerCase();
  return GEBIED_SLUG_PATROON.test(slug) ? slug : null;
}

function uniek<T>(lijst: readonly T[]): T[] {
  return [...new Set(lijst)];
}

/**
 * Dezelfde URL, met andere gebieden, in de kommavorm (`selected_area=1011,1012`).
 *
 * Alle overige parameters blijven staan - beschikbaarheid, woningtype, sortering -
 * zodat bewerken in de webapp niet stilletjes de filters weggooit die je op Funda
 * had gezet. Alleen `selected_area` wordt vervangen, en `search_result`
 * (de pagina) gaat eruit: pagina 1 draagt die niet.
 *
 * Zonder bestaande URL begint hij bij de kale koop-zoekpagina.
 */
export function urlMetGebieden(
  url: string | null | undefined,
  gebieden: readonly string[],
): string {
  let adres: URL;
  try {
    adres = new URL(url || FUNDA_ZOEK_BASIS);
  } catch {
    adres = new URL(FUNDA_ZOEK_BASIS);
  }

  adres.searchParams.delete('search_result');

  const schoon = uniek(gebieden.map(schoonSlug).filter((slug): slug is string => slug !== null));
  if (schoon.length === 0) adres.searchParams.delete(SELECTED_AREA);
  else adres.searchParams.set(SELECTED_AREA, schoon.join(','));

  return fundaUrlTekst(adres);
}

/**
 * Een URL als tekst, met de komma en de schuine streep in de querystring
 * leesbaar in plaats van `%2C` en `%2F`.
 *
 * `URLSearchParams` codeert beide, en dat mag van de standaard - maar Funda's
 * eigen kale vorm schrijft ze kaal (`selected_area=amsterdam/frans-halsbuurt`),
 * en de kommavorm is nagemeten zónder codering. Beide tekens zijn in een
 * querystring toegestaan; alleen deze twee worden teruggezet, al het andere
 * (`+` in `A+++`, spaties) blijft gecodeerd.
 */
export function fundaUrlTekst(adres: URL): string {
  const tekst = adres.toString();
  const vraagteken = tekst.indexOf('?');
  if (vraagteken < 0) return tekst;
  const query = tekst.slice(vraagteken).replace(/%2C/gi, ',').replace(/%2F/gi, '/');
  return tekst.slice(0, vraagteken) + query;
}

/* --- leesbaar -------------------------------------------------------------- */

/** Woorden die in een Nederlandse plaatsnaam klein blijven, behalve vooraan. */
const KLEINE_WOORDEN = new Set([
  'de',
  'het',
  'een',
  'van',
  'der',
  'den',
  'en',
  'aan',
  'op',
  'bij',
  'in',
  'te',
  'ten',
  'ter',
]);

/**
 * `amsterdam/frans-halsbuurt` -> `Frans Halsbuurt`.
 *
 * Een slug weet niet of een streepje een spatie was (Frans Halsbuurt) of een echt
 * streepje (Oud-West); hier wordt het een spatie. Wie het beter weet geeft
 * `namen` mee - de gebiedenlijst van Funda - en die wint dan.
 */
export function gebiedLabel(slug: string, namen?: ReadonlyMap<string, string>): string {
  const bekend = namen?.get(slug);
  if (bekend) return bekend;

  const laatste = slug.split('/').pop() ?? slug;
  return laatste
    .split('-')
    .filter((woord) => woord.length > 0)
    .map((woord, i) =>
      i > 0 && KLEINE_WOORDEN.has(woord) ? woord : woord.charAt(0).toUpperCase() + woord.slice(1),
    )
    .join(' ');
}

/** De gemeente uit een slug: `amsterdam/de-pijp` -> `amsterdam`. */
export function gemeenteVan(slug: string): string {
  return slug.split('/')[0] ?? slug;
}

/**
 * Of twee zoekopdrachten elkaar in gebied overlappen, en waar.
 *
 * `amsterdam` dekt `amsterdam/de-pijp`: een feed over de hele stad en een feed
 * over één buurt zien dezelfde woningen, en die springen dan heen en weer tussen
 * de twee datasets. Dat is de reden om dit op het scherm te zetten.
 */
export function overlappendeGebieden(a: readonly string[], b: readonly string[]): string[] {
  const gevonden: string[] = [];
  for (const x of a) {
    for (const y of b) {
      if (x === y || x.startsWith(`${y}/`) || y.startsWith(`${x}/`)) {
        gevonden.push(x.length >= y.length ? x : y);
      }
    }
  }
  return uniek(gevonden);
}

/* --- ritme ----------------------------------------------------------------- */

/** `720` -> `2× per dag`; `1440` -> `1× per dag`; `180` -> `elke 3 uur`. */
export function ritmeLabel(intervalMinuten: number): string {
  if (intervalMinuten >= 1440 && intervalMinuten % 1440 === 0) {
    const dagen = intervalMinuten / 1440;
    return dagen === 1 ? '1× per dag' : `elke ${dagen} dagen`;
  }
  /* "16× per dag" is rekenkundig juist en voor niemand een ritme; vanaf zes uur
     spreekt een mens in keren per dag, daaronder in uren of minuten. */
  if (intervalMinuten >= 360 && intervalMinuten < 1440 && 1440 % intervalMinuten === 0) {
    return `${1440 / intervalMinuten}× per dag`;
  }
  if (intervalMinuten % 60 === 0) return `elke ${intervalMinuten / 60} uur`;
  return `elke ${intervalMinuten} min`;
}

/** De keuzes die het scherm aanbiedt; de database kent elk aantal minuten. */
export const RITME_KEUZES: readonly { minuten: number; label: string }[] = [
  { minuten: 360, label: ritmeLabel(360) },
  { minuten: 720, label: ritmeLabel(720) },
  { minuten: 1440, label: ritmeLabel(1440) },
  { minuten: 2880, label: ritmeLabel(2880) },
  { minuten: 10_080, label: ritmeLabel(10_080) },
];
